import { inject, Injectable } from "@angular/core";
import { DoctorsService } from "../../../api/services";
import {
  CreateDoctorDto,
  FilterDoctorDto,
  ResponseDoctorDto,
  UpdateDoctorDto,
} from "../../../api/models";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";

const TENANT = environment.tenant;

@Injectable({
  providedIn: "root",
})
export class DoctorsApiService {
  /** Service for interacting with the doctors API */
  private readonly _doctorsService: DoctorsService = inject(DoctorsService);

  /**
   * Creates a new doctor
   * @param body the doctor data
   * @returns the created doctor
   */
  public create$(body: CreateDoctorDto): Observable<ResponseDoctorDto> {
    return this._doctorsService.doctorsControllerCreate({ body });
  }

  /**
   * Updates a doctor
   * @param id the doctor id
   * @param body the doctor data
   * @returns the updated doctor
   */
  public update$(
    id: string,
    body: UpdateDoctorDto,
  ): Observable<ResponseDoctorDto> {
    return this._doctorsService.doctorsControllerUpdate({
      tenant: TENANT,
      id,
      body,
    });
  }

  /**
   * Deletes a doctor
   * @param id the doctor id
   * @returns the deleted doctor
   */
  public delete$(id: string): Observable<void> {
    return this._doctorsService.doctorsControllerDelete({
      tenant: TENANT,
      id,
    });
  }

  /**
   * Retrieves a doctor
   * @param id the doctor id
   * @returns the doctor
   */
  public findOne$(id: string): Observable<ResponseDoctorDto> {
    return this._doctorsService.doctorsControllerFindOne({
      tenant: TENANT,
      id,
    });
  }

  /**
   * Filters doctors
   * @param filterDto the filter data
   * @returns the filtered doctors
   */
  public filter$(
    filterDto: Partial<FilterDoctorDto>,
  ): Observable<ResponseDoctorDto[]> {
    const body: FilterDoctorDto = {
      ...filterDto,
      tenant: TENANT,
    };

    return this._doctorsService.doctorsControllerFilter({
      body,
    });
  }
}
